import { useHistory } from 'react-router-dom';
import { Avatar, Popover, TopBar } from '@panviva/panviva-react-ui';
import logo from '../assets/images/logo.png';
import { useOktaAuth } from '@okta/okta-react';
import { useState, useEffect, useRef } from 'react';
import { RootState } from '../app/store';
import { useSelector } from 'react-redux';
import "./Header.scss";
import { LoadingIndicator } from './common/LoadingIndicator';
import pulseTitleLogo from "./../assets/images/pulse-title-logo.svg";

const Header = ({ menuOpen, setMenuOpen, ...props }: any) => {

    const { oktaAuth } = useOktaAuth();
    const history = useHistory();
    const location = history.location;
    const [userInfo, setUserInfo] = useState({ firstName: '', lastName: '', fullName: '', email: '' });
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = Boolean(anchorEl);
    const firstItemRef = useRef<HTMLLIElement>(null);
    const user = useSelector<RootState, any>(
        (state) => state?.users?.user || null
    );
    const loading = useSelector<RootState, boolean>(
        (state) => state?.users?.loading
    );

    useEffect(() => {

        if (user && Object.keys(user).length > 0) {
            localStorage.setItem('userDetailsEUM', JSON.stringify(user));
            const userNameSplit = user?.fullName?.split(' ') || [];
            const firstName = userNameSplit[0] || '';
            const lastName = userNameSplit[userNameSplit.length - 1] || '';
            setUserInfo({
                firstName: firstName || '',
                lastName: lastName || '',
                fullName: user.fullName || '',
                email: user?.emailAddress || ''
            });
        }
    }, [user]);

    const logout = async () => {
        await oktaAuth.signOut({
            postLogoutRedirectUri: `${window.location.origin}`
        });
        localStorage.setItem('accessToken', '');
        localStorage.removeItem('userRole');
        localStorage.removeItem('userDetails');
        localStorage.removeItem('userDetailsEUM');
        localStorage.setItem('logout', Date.now().toString());
    };

    const handleClickProfile = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleProfileKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setAnchorEl(e.currentTarget);
        } else if (e.key === 'Tab' && open && !e.shiftKey) {
            // When popover is open and user tabs forward, move focus to logout button
            e.preventDefault();
            firstItemRef.current?.focus();
        }
    };

    const handleLogoutKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            logout();
        } else if (e.key === 'Tab' && !e.shiftKey) {
            // Close popover when tabbing forward from logout
            e.preventDefault();
            handleClose();
        } else if (e.key === 'Tab' && e.shiftKey) {
            // Close popover when shift-tabbing backward from logout
            e.preventDefault();
            handleClose();
        }
    };

    const handlePopoverKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            handleClose();
        }
    };

    return (
        <>
            <LoadingIndicator busy={loading} />
            <div className="topbar-wrapper">
                <TopBar
                    style={{ height: 56 }}
                    navSize={56}
                    label={location.pathname.startsWith('/sidekick/chat-history') ? "Sidekick" : "Panviva Pulse"}
                    barButtons={
                        <button
                            data-testid='profile-icon'
                            className="profile-icon-wrapper"
                            onClick={handleClickProfile}
                            onKeyDown={handleProfileKeyDown}
                            type="button"
                            tabIndex={1}
                            aria-label={`User profile menu${userInfo.fullName ? `, ${userInfo.fullName}` : ''}`}
                            aria-expanded={open}
                            aria-haspopup="true">
                            <Avatar
                                firstName={userInfo.firstName}
                                lastName={userInfo.lastName}
                                className="avatar"
                            />
                        </button>}
                    logo={logo}
                    {...props}
                />
                {!location.pathname.startsWith('/sidekick/chat-history') && (
                    <img src={pulseTitleLogo} alt="Panviva Pulse Logo" className='pulse-title-logo' />
                )}
            </div>
            <Popover
                className="pv-popover"
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                onClose={() => handleClose()}
                aria-label="User profile menu"
            >
                <ul className="pv-popover-list" onKeyDown={handlePopoverKeyDown}>
                    {userInfo.fullName && (
                        <li className="pv-popover-item user-name" aria-label={`User name: ${userInfo.fullName}`}>
                            {userInfo.fullName}
                        </li>
                    )}
                    {userInfo.email && (
                        <li className="pv-popover-item user-email" aria-label={`Email: ${userInfo.email}`}>
                            {userInfo.email}
                        </li>
                    )}
                    <li
                        ref={firstItemRef}
                        className="pv-popover-item logout-button"
                        role="button"
                        tabIndex={0}
                        onClick={logout}
                        onKeyDown={handleLogoutKeyDown}
                        aria-label="Logout"
                    >
                        Logout
                    </li>
                </ul>
            </Popover>
        </>
    );
};

export default Header;
